import { Button, Grid2, Typography } from '@mui/material'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { Formik, Field, Form } from "formik";
import * as Yup from "yup";
import { TextFormField } from '../../../shared/components/formfields/TextFormField';
import { Grid_Gap, Input_Width } from '../../../shared/constants';
import { LocationSelectorField } from '../../../shared/components/LocationSelectorField';
import { useToaster } from '../../../shared/utils/ToasterProvider';
import { postAddTeacher } from '../../../assets/nodeApi/postApi/postSchoolApi';
import CustomDivider from '../../../shared/utils/CustomDivider';
import { useSchoolDropdownOptions } from '../options/SchoolOption';
import TableFilterTemplate from '../../../shared/utils/TableFilterTemplate';
import { NormalContainerWithHeader } from '../../../shared/components/student/NormalComponent';
import { useGetTeacherWithCourses } from '../../../assets/nodeApi/getApi/getSchoolApi';
import { useState } from 'react';
import MainDialogBox from '../../../shared/components/Dialog/MainDialogBox';
import { useDialog } from '../../../shared/hook/useDialog';
import { formatDateToDDMMYYYY } from '../../../shared/formatter';


const AddTeacherForm = () => {
    const queryClient=useQueryClient()
    const { open, handleClickOpen, handleClose } = useDialog();
    const { data: TeacherList, isLoading } = useGetTeacherWithCourses()
    const [teacherCourses, setTeacherCourses] = useState([])
    const TeacherOptions = TeacherList?.data?.map((item, index) => ({
        sr_no: index + 1,
        ...item
    })) ?? [];
    const { schoolTeacher } = useSchoolDropdownOptions();

    const { showToaster } = useToaster();
    const mutation = useMutation({
        mutationFn: postAddTeacher,
    });

    const handleSubmit = (values, { resetForm }) => {
        const { confirm_password, country_name,undefined, ...payload } = values;
        mutation.mutate(payload, {
            onSuccess: (response) => {
                queryClient.invalidateQueries({ queryKey: ["school/teachers-with-courses"] });
                queryClient.invalidateQueries({ queryKey: ["school/teachers"] });


                showToaster('success', response?.data?.message, 'success')
                resetForm();
            },
            onError: (err) => {
                console.error(err)
                showToaster(err?.response?.data?.message || 'Error occurred while submitting Form.', 'Error', 'error')
            },
        });
    };

    const initialValues = {
        firstname: "",
        lastname: "",
        username: "",
        email: "",
        mobile: "",
        country_name: "",
        state: "",
        city: "",
        address: "",
        zip: "",
        password: "",
        confirm_password: "",
    };

    const validationSchema = Yup.object().shape({
        firstname: Yup.string().trim().required("First name is required"),
        lastname: Yup.string().trim().required("Last name is required"),
        username: Yup.string().trim().required("Username is required"),
        email: Yup.string().email("Enter a valid email").required("Email is required"),
        mobile: Yup.string()
            .matches(/^[0-9]{10}$/, "Mobile must be 10 digits")
            .required("Mobile is required"),
        country_name: Yup.string().required("Country is required"),
        state: Yup.string().required("State is required"),
        city: Yup.string().required("City is required"),
        address: Yup.string().trim().required("Address is required"),
        zip: Yup.string()
            .matches(/^[0-9]{6}$/, "Zip must be 6 digits")
            .required("Zip is required"),
        password: Yup
            .string()
            .min(8, "Password must be at least 8 characters")
            .max(16, "Password must be at most 16 characters")
            .required("Password is required"),
        confirm_password: Yup
            .string()
            .oneOf([Yup.ref("password"), null], "Passwords must match")
            .required("Confirm Password is required"),
    });
    const teacherColumns = [
        {
            field: 'sr_no',
            headerName: 'Sl No',
            flex: 1,
            renderCell: (params) => <Typography variant="body2">{params.value}</Typography>,
        },
        {
            field: 'firstname',
            headerName: 'First Name',
            flex: 1,
            renderCell: (params) => <Typography variant="body2">{params.value}</Typography>,
        },
        {
            field: 'lastname',
            headerName: 'Last Name',
            flex: 1,
            renderCell: (params) => <Typography variant="body2">{params.value}</Typography>,
        },
        {
            field: 'email',
            headerName: 'Email',
            flex: 1,
            renderCell: (params) => <Typography variant="body2">{params.value}</Typography>,
        },
        {
            field: 'username',
            headerName: 'Username',
            flex: 1,
            renderCell: (params) => <Typography variant="body2">{params.value}</Typography>,
        },
        {
            field: 'mobile',
            headerName: 'Mobile',
            flex: 1,
            renderCell: (params) => <Typography variant="body2">{params.value}</Typography>,
        },

    ];
    const courseColumns = [
        {
            field: 'sr_no',
            headerName: 'Sl No',
            flex: 1,
            renderCell: (params) => <Typography variant="body2">{params.value}</Typography>,
        },
        {
            field: 'title',
            headerName: 'Course Name',
            flex: 1,
            renderCell: (params) => <Typography variant="body2">{params.value}</Typography>,
        },
        {
            field: 'no_of_teachers',
            headerName: 'No of teachers',
            flex: 1,
            renderCell: (params) => <Typography variant="body2">{params.value}</Typography>,
        },
        {
            field: 'start_date',
            headerName: 'Start Date',
            flex: 1,
            renderCell: (params) => <Typography variant="body2">{formatDateToDDMMYYYY(params.value)}</Typography>,
        },
        {
            field: 'end_date',
            headerName: 'End Date',
            flex: 1,
            renderCell: (params) => <Typography variant="body2">{formatDateToDDMMYYYY(params.value)}</Typography>,
        }

    ];
    const ViewTeacher = (params) => {
        handleClickOpen()
        setTeacherCourses(params?.assigned_courses)
    }
    return (
        <>
            <NormalContainerWithHeader headerText={"Add Teachers"}>
                <Formik
                    enableReinitialize={true}
                    initialValues={initialValues}
                    validationSchema={validationSchema}
                    onSubmit={handleSubmit}

                >
                    {({ resetForm }) => (
                        <Form>
                            <Grid2 container spacing={Grid_Gap}>
                                <Grid2 children size={Input_Width}>
                                    <Field
                                        name="firstname"
                                        component={TextFormField}
                                        label="First Name"
                                        isRequired={true}

                                    />
                                </Grid2>
                                <Grid2 children size={Input_Width}>
                                    <Field
                                        name="lastname"
                                        component={TextFormField}
                                        label="Last Name"
                                        isRequired={true}
                                    />
                                </Grid2>
                                <Grid2 children size={Input_Width}>
                                    <Field
                                        name="username"
                                        component={TextFormField}
                                        label="User Name"
                                        isRequired={true}
                                    />
                                </Grid2>
                                <Grid2 children size={Input_Width}>
                                    <Field
                                        name="email"
                                        component={TextFormField}
                                        label="Email"
                                        isRequired={true}
                                    />
                                </Grid2>
                                <Grid2 children size={Input_Width}>
                                    <Field
                                        name="mobile"
                                        component={TextFormField}
                                        label="Mobile"
                                        isRequired={true}
                                    />

                                </Grid2>
                                <LocationSelectorField
                                    countryField="country_name"
                                    stateField="state"
                                    cityField="city"
                                    isStoredId={false}
                                    Input_Width={Input_Width}
                                    zipField={"zip"}
                                    isZone={false}

                                />
                                <Grid2 children size={Input_Width}>
                                    <Field
                                        name="address"
                                        component={TextFormField}
                                        label="Address"
                                        isRequired={true}

                                    />
                                </Grid2>
                                <Grid2 children size={Input_Width}>
                                    <Field
                                        name="password"
                                        component={TextFormField}
                                        label="Password"
                                        isRequired={true}
                                        type={"password"}
                                    />
                                </Grid2>
                                <Grid2 children size={Input_Width}>
                                    <Field
                                        name="confirm_password"
                                        component={TextFormField}
                                        label="Confirm Password"
                                        isRequired={true}
                                        type={"password"}
                                    />
                                </Grid2>
                            </Grid2>
                            <CustomDivider />
                            <Grid2 container spacing={Grid_Gap}>
                                <Grid2 children size={Input_Width} >
                                    <Button variant='outlined' fullWidth onClick={() => { resetForm() }} disabled={mutation.isPending}>
                                        Cancel
                                    </Button>
                                </Grid2>
                                <Grid2 children size={Input_Width}>
                                    <Button variant='contained' fullWidth type="submit" disabled={mutation.isPending}>
                                        {mutation.isPending ? "Submitting..." : "Submit"}
                                    </Button>
                                </Grid2>
                            </Grid2>
                        </Form>
                    )}
                </Formik>
            </NormalContainerWithHeader>
            <CustomDivider />
            <TableFilterTemplate
                title="Teacher List"
                columns={teacherColumns}
                rowsData={TeacherOptions || []}
                isLoading={isLoading}
                dataGridProps={{
                    onRowClick: (params) => ViewTeacher(params?.row)
                }}
            />

            <MainDialogBox open={open} handleClose={handleClose} width="60%">
                <TableFilterTemplate
                    title="Course List"
                    columns={courseColumns}
                    rowsData={teacherCourses || []}
                    isLoading={isLoading}
                    boxshodow='none'

                />
            </MainDialogBox>
        </>
    )
}

export default AddTeacherForm
